import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Grid2, Paper, TextField, Button, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Typography, MenuItem, Dialog, DialogTitle, DialogContent,
    DialogActions, Alert, Snackbar, IconButton, Tooltip
} from '@mui/material';
import {
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import ModernNavbar from '../setup/ModernNavbar';
import HeadingTest from '../../BuiltInComponent/HeadingTest';
import {
    getAllPaidLeaveRequests,
    updateLeaveStatus,
    clearError,
    clearSuccess
} from '../../features/PayrollManagement/empPaidLeavesSlice';
import { getDepartments } from '../../features/AdminManagement/departmentSlice';
import { getLeaves } from '../../features/AdminManagement/leaveSlice';

const LeaveApprovalManagement = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { allPaidLeaveRequests, loading, error, success } = useSelector((state) => state.empPaidLeaves);
    const { departments } = useSelector((state) => state.dept);
    const { leaves } = useSelector((state) => state.leaves);

    const [activeTab, setActiveTab] = useState(0);
    const [filters, setFilters] = useState({
        deptID: '',
        leaveID: '',
        searchText: ''
    });
    const [approvalDialog, setApprovalDialog] = useState({
        open: false,
        leave: null,
        action: '' // 'Approved' or 'Rejected'
    });
    const [remarks, setRemarks] = useState('');
    const [viewDialog, setViewDialog] = useState({ open: false, leave: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(getDepartments());
        dispatch(getLeaves());
    }, [dispatch]);

    useEffect(() => {
        const status = ['Pending', 'Approved', 'Rejected'][activeTab];
        dispatch(getAllPaidLeaveRequests(status));
    }, [activeTab, dispatch, success]);

    useEffect(() => {
        if (success) {
            setSnackbar({ open: true, message: 'Leave status updated successfully!', severity: 'success' });
            dispatch(clearSuccess());
            setApprovalDialog({ open: false, leave: null, action: '' });
            setRemarks('');
        }
    }, [success, dispatch]);

    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: error, severity: 'error' });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleApprovalClick = (leave, action) => {
        setApprovalDialog({ open: true, leave, action });
        setRemarks('');
    };

    const handleApprovalSubmit = () => {
        if (approvalDialog.action === 'Rejected' && !remarks.trim()) {
            setSnackbar({ open: true, message: 'Remarks are required for rejection', severity: 'error' });
            return;
        }

        dispatch(updateLeaveStatus({
            paidLeaveID: approvalDialog.leave.paidLeaveID,
            approvalStatus: approvalDialog.action,
            approvedBy: user.empID,
            approvalRemarks: remarks.trim() || null
        }));
    };

    const handleViewDetails = (leave) => {
        setViewDialog({ open: true, leave });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'success';
            case 'Rejected': return 'error';
            case 'Pending': return 'warning';
            default: return 'default';
        }
    };

    const filteredRequests = allPaidLeaveRequests.filter(leave => {
        if (filters.deptID && leave.deptID != filters.deptID) return false;
        if (filters.leaveID && leave.leaveID != filters.leaveID) return false;
        if (filters.searchText) {
            const search = filters.searchText.toLowerCase();
            return (
                leave.empName?.toLowerCase().includes(search) ||
                leave.empCNIC?.includes(search) ||
                leave.leaveReason?.toLowerCase().includes(search)
            );
        }
        return true;
    });

    return (
        <>
            <ModernNavbar />
            <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
                <HeadingTest title="Leave Approval Management" />

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1E90FF', fontWeight: 600 }}>
                        Filters
                    </Typography>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <TextField
                                select
                                fullWidth
                                label="Department"
                                name="deptID"
                                value={filters.deptID}
                                onChange={handleFilterChange}
                            >
                                <MenuItem value="">All Departments</MenuItem>
                                {departments && departments.map(dept => (
                                    <MenuItem key={dept.deptID} value={dept.deptID}>
                                        {dept.deptName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <TextField
                                select
                                fullWidth
                                label="Leave Type"
                                name="leaveID"
                                value={filters.leaveID}
                                onChange={handleFilterChange}
                            >
                                <MenuItem value="">All Leave Types</MenuItem>
                                {leaves && leaves.map(leave => (
                                    <MenuItem key={leave.leaveID} value={leave.leaveID}>
                                        {leave.leaveDescription}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search by Name/CNIC/Reason"
                                name="searchText"
                                value={filters.searchText}
                                onChange={handleFilterChange}
                                placeholder="Type to search..."
                            />
                        </Grid2>
                    </Grid2>
                </Paper>

                {/* Tabs and Table */}
                <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            bgcolor: '#1E90FF',
                            '& .MuiTab-root': { color: 'white', fontWeight: 600 },
                            '& .Mui-selected': { color: 'white !important' }
                        }}
                    >
                        <Tab label="Pending Requests" />
                        <Tab label="Approved Leaves" />
                        <Tab label="Rejected Leaves" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>CNIC</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Leave Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Days</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Adjusted</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Applied Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRequests.length > 0 ? (
                                        filteredRequests.map((leave) => (
                                            <TableRow key={leave.paidLeaveID} hover>
                                                <TableCell>{leave.empName}</TableCell>
                                                <TableCell>{leave.empCNIC}</TableCell>
                                                <TableCell>{leave.deptName}</TableCell>
                                                <TableCell>{leave.desigName}</TableCell>
                                                <TableCell>{leave.leaveName || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" display="block">
                                                        From: {dayjs(leave.leaveFromDate).format('DD-MMM-YY')}
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        To: {dayjs(leave.leaveToDate).format('DD-MMM-YY')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={leave.totalDays} color="primary" size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    {leave.remainingLeaves !== null ? (
                                                        <Typography variant="caption">
                                                            {leave.remainingLeaves} / {leave.allowedDays}
                                                        </Typography>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={leave.adjustFromAllowedLeaves ? 'Yes' : 'No'}
                                                        color={leave.adjustFromAllowedLeaves ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {dayjs(leave.appliedDate).format('DD-MMM-YYYY')}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                color="info"
                                                                onClick={() => handleViewDetails(leave)}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {activeTab === 0 && (
                                                            <>
                                                                <Tooltip title="Approve">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="success"
                                                                        onClick={() => handleApprovalClick(leave, 'Approved')}
                                                                    >
                                                                        <ApproveIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Reject">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleApprovalClick(leave, 'Rejected')}
                                                                    >
                                                                        <RejectIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                <Typography color="textSecondary">
                                                    No leave requests found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>

                {/* Approval Dialog */}
                <Dialog
                    open={approvalDialog.open}
                    onClose={() => setApprovalDialog({ open: false, leave: null, action: '' })}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: '#1E90FF', color: 'white' }}>
                        {approvalDialog.action === 'Approved' ? 'Approve Leave' : 'Reject Leave'}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        {approvalDialog.leave && (
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Employee:</strong> {approvalDialog.leave.empName}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Leave Period:</strong>{' '}
                                    {dayjs(approvalDialog.leave.leaveFromDate).format('DD-MMM-YYYY')} to{' '}
                                    {dayjs(approvalDialog.leave.leaveToDate).format('DD-MMM-YYYY')}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Total Days:</strong> {approvalDialog.leave.totalDays}
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label={`Remarks ${approvalDialog.action === 'Rejected' ? '(Required)' : '(Optional)'}`}
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    required={approvalDialog.action === 'Rejected'}
                                    placeholder="Enter your remarks..."
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setApprovalDialog({ open: false, leave: null, action: '' })}
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApprovalSubmit}
                            variant="contained"
                            disabled={loading}
                            sx={{
                                bgcolor: approvalDialog.action === 'Approved' ? '#4caf50' : '#f44336',
                                '&:hover': {
                                    bgcolor: approvalDialog.action === 'Approved' ? '#45a049' : '#da190b'
                                }
                            }}
                        >
                            {loading ? 'Processing...' : `Confirm ${approvalDialog.action}`}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog
                    open={viewDialog.open}
                    onClose={() => setViewDialog({ open: false, leave: null })}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: '#1E90FF', color: 'white' }}>
                        Leave Application Details
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        {viewDialog.leave && (
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Employee Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.leave.empName}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">CNIC</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.leave.empCNIC}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Department</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.leave.deptName}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Designation</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.leave.desigName}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Leave Type</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.leave.leaveName || 'N/A'}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Leave Balance</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {viewDialog.leave.remainingLeaves !== null
                                            ? `${viewDialog.leave.remainingLeaves} / ${viewDialog.leave.allowedDays} days`
                                            : 'N/A'}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">From Date</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {dayjs(viewDialog.leave.leaveFromDate).format('DD-MMM-YYYY')}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">To Date</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {dayjs(viewDialog.leave.leaveToDate).format('DD-MMM-YYYY')}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Total Days</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.leave.totalDays}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Adjust from Allowed Leaves</Typography>
                                    <Chip
                                        label={viewDialog.leave.adjustFromAllowedLeaves ? 'Yes' : 'No'}
                                        color={viewDialog.leave.adjustFromAllowedLeaves ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography variant="body2" color="textSecondary">Leave Reason</Typography>
                                    <Paper sx={{ p: 2, bgcolor: '#f9f9f9', mt: 1 }}>
                                        <Typography variant="body1">{viewDialog.leave.leaveReason}</Typography>
                                    </Paper>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Applied Date</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {dayjs(viewDialog.leave.appliedDate).format('DD-MMM-YYYY HH:mm')}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography variant="body2" color="textSecondary">Status</Typography>
                                    <Chip
                                        label={viewDialog.leave.approvalStatus}
                                        color={getStatusColor(viewDialog.leave.approvalStatus)}
                                        size="small"
                                    />
                                </Grid2>
                                {viewDialog.leave.approvalRemarks && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography variant="body2" color="textSecondary">Approval Remarks</Typography>
                                        <Paper sx={{ p: 2, bgcolor: '#f9f9f9', mt: 1 }}>
                                            <Typography variant="body1">{viewDialog.leave.approvalRemarks}</Typography>
                                        </Paper>
                                    </Grid2>
                                )}
                                {viewDialog.leave.approvedByName && (
                                    <Grid2 size={{ xs: 6 }}>
                                        <Typography variant="body2" color="textSecondary">Approved By</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {viewDialog.leave.approvedByName}
                                        </Typography>
                                    </Grid2>
                                )}
                                {viewDialog.leave.approvalDate && (
                                    <Grid2 size={{ xs: 6 }}>
                                        <Typography variant="body2" color="textSecondary">Approval Date</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {dayjs(viewDialog.leave.approvalDate).format('DD-MMM-YYYY HH:mm')}
                                        </Typography>
                                    </Grid2>
                                )}
                            </Grid2>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setViewDialog({ open: false, leave: null })} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default LeaveApprovalManagement;
